import { useMemo } from "react";
import { useAtomValue } from "jotai";
import { selectAtom } from "jotai/utils";
import { questionnaireAnswersAtom } from "../state/questionnaireAnswersAtom";
import type { Answers } from "../types";

/**
 * Get answers for specific questions only. Only re-renders when those answers change.
 *
 * @example const { name, email } = useAnswersForQuestions(['name', 'email']);
 * @see useAnswer - single answer
 * @see useAnswers - all answers
 */
export function useAnswersForQuestions(questionNames: string[]): Answers {
  // Create a stable key from the array to avoid recreating the atom unnecessarily
  const questionNamesKey = questionNames.join(",");

  const answersAtom = useMemo(
    () =>
      selectAtom(
        questionnaireAnswersAtom,
        allAnswers => {
          const subset: Answers = {};
          for (const name of questionNames) {
            if (name in allAnswers) {
              subset[name] = allAnswers[name];
            }
          }
          return subset;
        },
        (prev, next) => {
          // Custom equality: only trigger re-render if watched answers changed
          if (Object.keys(prev).length !== Object.keys(next).length) {
            return false;
          }
          return questionNames.every(name => prev[name] === next[name]);
        },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questionNamesKey], // Use stable string key instead of array reference
  );

  return useAtomValue(answersAtom);
}
